/**
 * ConfirmProvider
 *
 * Promise-based styled confirmation dialog to replace blocking window.confirm().
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete break period?',
 *     message: 'This cannot be undone.',
 *     confirmText: 'Delete',
 *     danger: true,
 *   });
 *   if (!ok) return;
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertTriangle } from 'react-icons/fi';

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return ctx;
};

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 100001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.5);
`;

const Dialog = styled(motion.div)`
  width: 100%;
  max-width: 420px;
  background: var(--background-primary, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  padding: 1.5rem;
  font-family: 'Inter', sans-serif;
`;

const IconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.875rem;
  background: ${(p) => (p.$danger ? 'rgba(224, 49, 49, 0.12)' : 'rgba(189, 150, 85, 0.14)')};
  color: ${(p) => (p.$danger ? 'var(--error-color, #e03131)' : 'var(--primary-color)')};

  svg {
    width: 22px;
    height: 22px;
  }
`;

const Title = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary, #111827);
`;

const Body = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--text-secondary, #4b5563);
  white-space: pre-line;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
`;

const BaseButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  border: 1px solid transparent;
`;

const CancelButton = styled(BaseButton)`
  background: var(--background-primary, #fff);
  border-color: var(--border-color, #d1d5db);
  color: var(--text-secondary, #4b5563);

  &:hover {
    background: var(--background-tertiary, #f9fafb);
  }
`;

const ConfirmButton = styled(BaseButton)`
  color: #fff;
  background: ${(p) => (p.$danger ? 'var(--error-color, #e03131)' : 'var(--primary-color)')};
  border-color: ${(p) => (p.$danger ? 'var(--error-color, #e03131)' : 'var(--primary-color)')};

  &:hover {
    filter: brightness(0.96);
  }
`;

const DEFAULTS = {
  title: 'Are you sure?',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  danger: false,
};

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({ ...DEFAULTS, ...options });
    });
  }, []);

  const settle = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setDialog(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AnimatePresence>
        {dialog && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => settle(false)}
          >
            <Dialog
              role="dialog"
              aria-modal="true"
              aria-label={dialog.title}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.18 }}
              onClick={(e) => e.stopPropagation()}
            >
              <IconWrap $danger={dialog.danger}>
                <FiAlertTriangle />
              </IconWrap>
              <Title>{dialog.title}</Title>
              {dialog.message && <Body>{dialog.message}</Body>}
              <Actions>
                <CancelButton onClick={() => settle(false)}>{dialog.cancelText}</CancelButton>
                <ConfirmButton $danger={dialog.danger} onClick={() => settle(true)} autoFocus>
                  {dialog.confirmText}
                </ConfirmButton>
              </Actions>
            </Dialog>
          </Overlay>
        )}
      </AnimatePresence>
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
